import { useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { HydratedCallRecord } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { useMutation } from 'react-query';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { TagsUtils, TagsV2 } from '@frontend/api-tag';
import { generateTrackingId } from '@frontend/call-intel-core';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Chip, useAlert, Button, Text } from '@frontend/design-system';
import { RecentCallsTagSelector } from '../recent-calls-tag-selector';

type Props = {
  call: HydratedCallRecord;
};

export const CallInsightTagsSection = ({ call }: Props) => {
  const { t } = useTranslation('call-insight-panel');
  const alert = useAlert();

  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);

  const { data: allTagsData } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      enabled: !!selectedTagIds.length,
    },
  });

  const tagMap = useMemo(() => {
    return (
      allTagsData?.tags.reduce((acc, tag) => {
        acc[tag.id] = tag;
        return acc;
      }, {} as Record<string, (typeof allTagsData.tags)[number]>) ?? {}
    );
  }, [allTagsData]);

  const displayedTags = selectedTagIds.map((tagId) => tagMap[tagId]).filter(Boolean);

  const { mutate: applyTag } = useMutation({
    mutationFn: (tag: Tag) =>
      PhoneCallsApi.applyCallRecordTags({
        channelId: call.channelId ?? '',
        tagIds: [tag.id],
        callRecordLocationId: call.locationId ?? '',
        startTime: call.startedAt ?? '',
      }),
    onSuccess: (_, addedTag) => {
      setSelectedTagIds((prev) => [...prev, addedTag.id]);
      alert.success(t('Tag added successfully.'));
    },
    onError: () => {
      alert.error(t('Failed to add tag.'));
    },
  });

  const { mutate: removeTag } = useMutation({
    mutationFn: (tag: Tag) =>
      PhoneCallsApi.removeCallRecordTags({
        channelId: call.channelId ?? '',
        tagIds: [tag.id],
        callRecordLocationId: call.locationId ?? '',
        startTime: call.startedAt ?? '',
      }),
    onSuccess: (_, removedTag) => {
      setSelectedTagIds((prev) => prev.filter((id) => id !== removedTag.id));
      alert.success(t('Successfully removed tag.'));
    },
    onError: () => {
      alert.error(t('Failed to remove tag.'));
    },
  });

  useEffect(() => {
    setSelectedTagIds(call.tagIds ?? []);
  }, [call.tagIds]);

  return (
    <div>
      {displayedTags.length === 0 && isEditing && (
        <div style={{ padding: theme.spacing(1) }}>
          <RecentCallsTagSelector
            onClose={() => setIsEditing(false)}
            onSelect={() => setIsEditing(false)}
            onApplyTag={applyTag}
            existingTagIds={displayedTags.map((t) => t.id)}
          />
        </div>
      )}
      {displayedTags.length > 0 ? (
        <div css={styles.tagsRow}>
          {displayedTags.map((tag) => (
            <Chip.Tag
              key={tag.id}
              isRemovable
              onRemoveClick={() => removeTag(tag)}
              color={TagsUtils.convertStringToTagColor(tag.color)}
            >
              {tag.name}
            </Chip.Tag>
          ))}
          {isEditing && (
            <RecentCallsTagSelector
              onClose={() => setIsEditing(false)}
              onSelect={() => setIsEditing(false)}
              existingTagIds={displayedTags.map((t) => t.id)}
              onApplyTag={applyTag}
            />
          )}
          <Button
            iconName='label'
            css={styles.addTagBtn}
            variant='secondary'
            onClick={() => setIsEditing(true)}
            trackingId={generateTrackingId({
              prefix: 'call-insight-panel',
              component: 'add-tag',
            })}
          />
        </div>
      ) : (
        <div css={styles.addTagBtnWrapper}>
          <Button
            iconName='label'
            css={styles.addTagBtn}
            variant='secondary'
            onClick={() => setIsEditing(true)}
            trackingId={generateTrackingId({
              prefix: 'call-insight-panel',
              component: 'add-tag',
            })}
          />
          <Text size='medium' color='subdued'>
            {t('Tag this call')}
          </Text>
        </div>
      )}
    </div>
  );
};

const styles = {
  addTagBtnWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  addTagBtn: css`
    height: ${theme.spacing(3)};
    width: ${theme.spacing(3)};
    svg {
      color: ${theme.colors.neutral90};
    }
  `,
  tagsRow: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
};
